import {
    clearKeyword,
    clearSelection,
    setKeyword,
    setPage,
    setPageSize,
} from '../reducers';

import {
    selectionSelector,
    selectVisibility,
    visibilityStateSelector,
} from '../selectors';

import {columns} from '../tables/columns';
import {DomainName} from '../types';
import {
    useAppDispatch,
    useAppSelector
} from '../../../hooks/useTypedReactRedux';

import {
    useDeleteDomainNameMutation,
    useDomainNamesQuery
} from './useDomainNamesQuery';

// FNP-942: records come from React Query; selection, paging and keyword stay in
// Redux. The return shape is unchanged so the table, buttons, inputs and the
// dashboard widget that consume this hook did not have to change.
export const useDomainNames = (registrarId?: string) => {

    const dispatch = useAppDispatch();

    const selection = useAppSelector((state) => selectionSelector(state));
    const visibility = useAppSelector((state) => visibilityStateSelector(state));

    const query = useDomainNamesQuery();

    const {
        data,
        isLoading,
        isError,
        error,
        refetch,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        isRefetching,
    } = query;

    const deleteMutation = useDeleteDomainNameMutation();

    // FNP-1282: flatten the pages useInfiniteQuery accumulated. The `'items' in
    // page` guard is not defensive noise — a page object that came back partial
    // would otherwise make this flatMap throw inside a render.
    const records: DomainName[] = (data?.pages ?? [])
        .flatMap(page => (page && 'items' in page) ? (page.items ?? []) : [])
        .filter(Boolean);

    const {
        items,
        visibleItems,
        limit,
        keyword,
        page,
        totalPages,
        visibleCount,
    } = selectVisibility(visibility, records, registrarId);

    // FETCH — loads the NEXT page under token pagination; falls back to a
    // refetch when everything the server has is already loaded, which is what
    // the Reload button and the mount effects want.
    const fetchRecords = () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
            return;
        }
        refetch();
    };

    const deleteRecords = () => {
        if (Object.keys(selection).length > 0) {
            for (const key in selection) {
                deleteMutation.mutate(key);
            }
            // Clear the checkboxes once the deletions are issued — otherwise the
            // selection keeps naming records that no longer exist.
            dispatch(clearSelection());
        }
    };


    // FILTERING AND PAGING
    const handleFilterByKeyword = (keyword: string) => {
        if (keyword !== '') {
            dispatch(setKeyword(keyword));
        } else {
            dispatch(clearKeyword());
        }
    };

    const handleItemsPerPageChange = (pageSize: string | null) => {
        dispatch(setPageSize(pageSize));
    };

    const handlePageChange = (page: number) => {
        dispatch(setPage(page));
    };

    // BATCH ACTIONS
    const handleBatchActionCancel = () => {
        dispatch(clearSelection());
    };

    return {
        items,
        visibleItems,
        limit,
        loading: isLoading,
        // FNP-1282: surfaced so the table can tell a FAILED load apart from an
        // empty one. Previously dropped at this boundary, which is why three
        // different states all rendered as the same empty table.
        isError,
        error,
        isFetchingNextPage,
        isRefetching,
        // Derived from hasNextPage, never from items.length — a sentinel, since
        // the footer only needs it to be truthy or null.
        nextToken: hasNextPage ? 'has-more' : null,
        reload: () => refetch(),
        keyword,
        page,
        totalPages,
        visibleCount,
        columns,
        fetchRecords,
        deleteRecords,
        handleFilterByKeyword,
        handleItemsPerPageChange,
        handleBatchActionCancel,
        handlePageChange,
    };
};
